// src/pages/vendor/Payouts.jsx - ENHANCED WITH BALANCE DETAILS
import React, { useState, useEffect } from 'react';
import { vendorApi } from '../../api/vendorApi';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Stack,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  Divider,
  LinearProgress,
  CircularProgress,
  Fade,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add,
  CheckCircle,
  Cancel,
  Pending,
  Edit,
  DeleteOutline,
  AccountBalance,
  AccountBalanceWallet,
  AttachMoney,
  TrendingUp,
  Info,
  ExpandMore,
  Refresh,
  Receipt,
  Payment,
  Warning,
  Check,
  Close,
  HelpOutline,
} from '@mui/icons-material';

const Payouts = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [balance, setBalance] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalWithdrawn, setTotalWithdrawn] = useState(0);
  const [pendingPayouts, setPendingPayouts] = useState(0);
  const [processingPayouts, setProcessingPayouts] = useState(0);
  const [commissionRate, setCommissionRate] = useState(0);
  const [commissionAmount, setCommissionAmount] = useState(0);
  
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [amount, setAmount] = useState('');
  const [success, setSuccess] = useState(null);
  const [expandedStats, setExpandedStats] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Edit Payout State
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingPayout, setEditingPayout] = useState(null);
  const [editAmount, setEditAmount] = useState('');

  // Minimum payout amount
  const [minPayout, setMinPayout] = useState(500);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await vendorApi.getPayouts();
      
      if (response) {
        // Extract balance with detailed breakdown
        const extractedBalance = response.balance ?? response.totalBalance ?? response.availableBalance ?? 0;
        const extractedHistory = response.history ?? response.payouts ?? response.data ?? [];
        const extractedStats = response.stats || {};
        
        setBalance(typeof extractedBalance === 'string' ? parseFloat(extractedBalance) || 0 : extractedBalance);
        setPayouts(Array.isArray(extractedHistory) ? extractedHistory : []);
        
        // Detailed balance breakdown
        setTotalRevenue(extractedStats.totalRevenue || response.totalRevenue || 0);
        setTotalWithdrawn(extractedStats.totalWithdrawn || response.totalWithdrawn || 0);
        setPendingPayouts(extractedStats.pendingAmount || response.pendingAmount || 0);
        setProcessingPayouts(extractedStats.processingAmount || response.processingAmount || 0);
        setCommissionRate(extractedStats.commissionRate || response.commissionRate || 0);
        setCommissionAmount(extractedStats.commissionAmount || response.commissionAmount || 0);
        setMinPayout(extractedStats.minimumPayout || response.minimumPayout || 500);
      }
    } catch (err) {
      setError(err.message || 'Failed to load payout data');
      console.error('Error fetching payouts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  };

  const handleRequestPayout = async () => {
    const amountNum = parseFloat(amount);
    if (!amount || amountNum <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (amountNum < minPayout) {
      setError(`Minimum withdrawal amount is ${minPayout} ETB`);
      return;
    }
    if (amountNum > balance) {
      setError('Insufficient available balance');
      return;
    }

    try {
      await vendorApi.requestPayout({ amount: amountNum });
      setSuccess('Withdrawal request submitted successfully!');
      setOpenDialog(false);
      setAmount('');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to request payout');
    }
  };

  const handleSaveEdit = async () => {
    const newAmount = parseFloat(editAmount);
    if (!newAmount || newAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (newAmount < minPayout) {
      setError(`Minimum withdrawal amount is ${minPayout} ETB`);
      return;
    }
    if (newAmount > balance) {
      setError('Insufficient available balance');
      return;
    }

    try {
      await vendorApi.updatePayoutAmount(editingPayout.id, newAmount);
      setSuccess('Payout amount updated successfully!');
      setOpenEditDialog(false);
      setEditingPayout(null);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update payout amount');
    }
  };

  const handleCancelPayout = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this payout request?')) return;

    try {
      await vendorApi.cancelPayout(id);
      setSuccess('Payout request cancelled successfully.');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to cancel payout request');
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `ETB ${(typeof amount === 'string' ? parseFloat(amount) : (amount || 0)).toFixed(2)}`;
  };

  // Get status info
  const getStatusInfo = (status) => {
    const statusMap = {
      pending: { label: 'Pending', color: 'warning', icon: <Pending /> },
      processing: { label: 'Processing', color: 'info', icon: <Pending /> },
      completed: { label: 'Completed', color: 'success', icon: <CheckCircle /> },
      failed: { label: 'Failed', color: 'error', icon: <Cancel /> },
      cancelled: { label: 'Cancelled', color: 'error', icon: <Cancel /> },
    };
    return statusMap[status?.toLowerCase()] || statusMap.pending;
  };

  // Calculate progress percentage
  const getProgressPercentage = () => {
    if (totalRevenue === 0) return 0;
    const remaining = balance + totalWithdrawn;
    return Math.min((remaining / totalRevenue) * 100, 100);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading payout information...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 8, sm: 10 } }}>
      {/* Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              Payouts & Withdrawals
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track your earnings and request payouts
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={isRefreshing}
              size={isMobile ? 'small' : 'medium'}
            >
              {isRefreshing ? <CircularProgress size={20} /> : 'Refresh'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Error/Success Messages */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }} 
          onClose={() => setError(null)}
          action={
            <Button color="inherit" size="small" onClick={() => setError(null)}>
              Dismiss
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }} 
          onClose={() => setSuccess(null)}
          action={
            <Button color="inherit" size="small" onClick={() => setSuccess(null)}>
              Dismiss
            </Button>
          }
        >
          {success}
        </Alert>
      )}

      {/* Main Balance Card */}
      <Card sx={{ 
        mb: 3, 
        bgcolor: 'primary.main', 
        color: 'white', 
        p: { xs: 2, sm: 3 }, 
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box
          sx={{
            position: 'absolute',
            right: -50,
            top: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.05)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            right: 20,
            bottom: -80,
            width: 150,
            height: 150,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.03)',
          }}
        />
        <CardContent sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack spacing={1}>
                <Typography variant="caption" sx={{ opacity: 0.8, letterSpacing: 1 }}>
                  AVAILABLE BALANCE
                </Typography>
                <Typography variant="h2" fontWeight="bold" sx={{ fontSize: { xs: '2.5rem', sm: '3.5rem' } }}>
                  {formatCurrency(balance)}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Minimum withdrawal: {formatCurrency(minPayout)}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setOpenDialog(true)}
                    sx={{ 
                      bgcolor: 'white', 
                      color: 'primary.main', 
                      '&:hover': { bgcolor: 'grey.100' },
                      borderRadius: 2,
                      px: 3,
                    }}
                    disabled={balance < minPayout}
                  >
                    Request Withdrawal
                  </Button>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Stack direction="row" spacing={2} justifyContent={isMobile ? 'flex-start' : 'flex-end'}>
                <Box sx={{ textAlign: 'center', px: 2 }}>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>Total Revenue</Typography>
                  <Typography variant="h6" fontWeight="bold">{formatCurrency(totalRevenue)}</Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                <Box sx={{ textAlign: 'center', px: 2 }}>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>Withdrawn</Typography>
                  <Typography variant="h6" fontWeight="bold">{formatCurrency(totalWithdrawn)}</Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                <Box sx={{ textAlign: 'center', px: 2 }}>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>Pending</Typography>
                  <Typography variant="h6" fontWeight="bold">{formatCurrency(pendingPayouts)}</Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Balance Breakdown Accordion */}
      <Accordion 
        expanded={expandedStats} 
        onChange={() => setExpandedStats(!expandedStats)}
        sx={{ mb: 3, borderRadius: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AccountBalanceWallet color="primary" />
            <Typography fontWeight="600">Balance Breakdown</Typography>
            <Chip label="Detailed" size="small" variant="outlined" />
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Revenue Breakdown */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                Revenue Breakdown
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">Total Revenue</Typography>
                    <Typography variant="body2" fontWeight="bold">{formatCurrency(totalRevenue)}</Typography>
                  </Stack>
                  <LinearProgress 
                    variant="determinate" 
                    value={100} 
                    sx={{ mt: 0.5, height: 6, borderRadius: 3, bgcolor: 'grey.200' }}
                  />
                </Box>
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">Commission ({commissionRate}%)</Typography>
                    <Typography variant="body2" color="error.main">{formatCurrency(commissionAmount)}</Typography>
                  </Stack>
                  <LinearProgress 
                    variant="determinate" 
                    value={commissionRate} 
                    sx={{ mt: 0.5, height: 6, borderRadius: 3, bgcolor: 'grey.200' }}
                    color="error"
                  />
                </Box>
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">Net Revenue</Typography>
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      {formatCurrency(totalRevenue - commissionAmount)}
                    </Typography>
                  </Stack>
                  <LinearProgress 
                    variant="determinate" 
                    value={getProgressPercentage()} 
                    sx={{ mt: 0.5, height: 6, borderRadius: 3, bgcolor: 'grey.200' }}
                    color="success"
                  />
                </Box>
              </Stack>
            </Grid>

            {/* Balance Status */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                Balance Status
              </Typography>
              <List dense disablePadding>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <AccountBalanceWallet color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Available Balance" 
                    secondary={formatCurrency(balance)}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ fontWeight: 'bold', color: 'success.main' }}
                  />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Pending color="warning" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Pending Payouts" 
                    secondary={formatCurrency(pendingPayouts)}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ color: 'warning.main' }}
                  />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Pending color="info" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Processing Payouts" 
                    secondary={formatCurrency(processingPayouts)}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ color: 'info.main' }}
                  />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckCircle color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Total Withdrawn" 
                    secondary={formatCurrency(totalWithdrawn)}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ fontWeight: 'bold', color: 'success.main' }}
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Quick Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <AccountBalanceWallet color="primary" sx={{ fontSize: { xs: 28, sm: 36 }, mb: 0.5 }} />
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                {formatCurrency(balance)}
              </Typography>
              <Typography variant="caption" color="text.secondary">Available</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <TrendingUp color="success" sx={{ fontSize: { xs: 28, sm: 36 }, mb: 0.5 }} />
              <Typography variant="h6" fontWeight="bold" color="success.main">
                {formatCurrency(totalRevenue)}
              </Typography>
              <Typography variant="caption" color="text.secondary">Total Revenue</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <Pending color="warning" sx={{ fontSize: { xs: 28, sm: 36 }, mb: 0.5 }} />
              <Typography variant="h6" fontWeight="bold" color="warning.main">
                {formatCurrency(pendingPayouts + processingPayouts)}
              </Typography>
              <Typography variant="caption" color="text.secondary">In Progress</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <AttachMoney color="error" sx={{ fontSize: { xs: 28, sm: 36 }, mb: 0.5 }} />
              <Typography variant="h6" fontWeight="bold" color="error.main">
                {formatCurrency(commissionAmount)}
              </Typography>
              <Typography variant="caption" color="text.secondary">Commission</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Withdrawal History */}
      <Paper sx={{ borderRadius: 3, p: { xs: 2, sm: 3 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">Withdrawal History</Typography>
          <Chip 
            label={`${payouts.length} requests`} 
            size="small" 
            variant="outlined" 
          />
        </Stack>
        <TableContainer>
          <Table size={isMobile ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!payouts || payouts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                    <Stack alignItems="center" spacing={1}>
                      <Receipt sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
                      <Typography color="text.secondary">No payout history yet</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Your withdrawals will appear here once you request a payout
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                payouts.map((p, index) => {
                  const statusInfo = getStatusInfo(p.status);
                  return (
                    <TableRow key={p.id || index} hover>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {p.requestedAt ? new Date(p.requestedAt).toLocaleDateString() : 'N/A'}
                        </Typography>
                        {p.requestedAt && (
                          <Typography variant="caption" color="text.secondary">
                            {new Date(p.requestedAt).toLocaleTimeString()}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="bold" color={p.status === 'completed' ? 'success.main' : 'text.primary'}>
                          {formatCurrency(p.amount)}
                        </Typography>
                        {p.transactionRef && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Ref: {p.transactionRef}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{p.method || 'Bank Transfer'}</TableCell>
                      <TableCell>
                        <Chip 
                          icon={statusInfo.icon}
                          label={statusInfo.label} 
                          size="small" 
                          color={statusInfo.color}
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {p.status === 'pending' && (
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Edit Amount">
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={() => {
                                  setEditingPayout(p);
                                  setEditAmount(p.amount.toString());
                                  setOpenEditDialog(true);
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel Request">
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => handleCancelPayout(p.id)}
                              >
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        )}
                        {p.status === 'processing' && (
                          <Tooltip title="Processing - Please wait">
                            <Chip 
                              icon={<Pending />}
                              label="Processing"
                              size="small"
                              color="info"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Request Withdrawal Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AttachMoney color="primary" />
            <Typography variant="h6">Request Withdrawal</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity="info" icon={<Info />}>
              <Stack spacing={0.5}>
                <Typography variant="body2">
                  Available balance: <strong>{formatCurrency(balance)}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Minimum withdrawal: {formatCurrency(minPayout)}
                </Typography>
              </Stack>
            </Alert>
            
            {balance < minPayout && (
              <Alert severity="warning" icon={<Warning />}>
                Your balance ({formatCurrency(balance)}) is below the minimum withdrawal amount of {formatCurrency(minPayout)}.
                Continue selling to reach the minimum threshold.
              </Alert>
            )}

            <TextField 
              fullWidth 
              type="number" 
              label="Amount (ETB)" 
              value={amount} 
              onChange={(e) => setAmount(e.target.value)} 
              disabled={balance < minPayout}
              helperText={
                balance < minPayout 
                  ? `Minimum withdrawal is ${formatCurrency(minPayout)}` 
                  : `Enter amount between ${formatCurrency(minPayout)} and ${formatCurrency(balance)}`
              }
              inputProps={{
                min: minPayout,
                max: balance,
                step: 0.01,
              }}
              error={amount && (parseFloat(amount) < minPayout || parseFloat(amount) > balance)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
          <Button onClick={() => setOpenDialog(false)} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleRequestPayout} 
            disabled={
              balance < minPayout || 
              !amount || 
              parseFloat(amount) < minPayout ||
              parseFloat(amount) > balance
            }
            fullWidth={isMobile}
            startIcon={<Payment />}
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Amount Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Edit color="primary" />
            <Typography variant="h6">Edit Payout Amount</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity="info">
              <Typography variant="body2">
                Current request: <strong>{formatCurrency(editingPayout?.amount)}</strong>
              </Typography>
            </Alert>
            <Typography variant="body2">
              Available balance: <strong>{formatCurrency(balance)}</strong>
            </Typography>
            <TextField 
              fullWidth 
              type="number" 
              label="New Amount (ETB)" 
              value={editAmount} 
              onChange={(e) => setEditAmount(e.target.value)} 
              helperText={`Minimum: ${formatCurrency(minPayout)}`}
              inputProps={{
                min: minPayout,
                max: balance,
                step: 0.01,
              }}
              error={editAmount && (parseFloat(editAmount) < minPayout || parseFloat(editAmount) > balance)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
          <Button onClick={() => setOpenEditDialog(false)} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveEdit} 
            disabled={
              balance < minPayout || 
              !editAmount || 
              parseFloat(editAmount) < minPayout ||
              parseFloat(editAmount) > balance
            }
            fullWidth={isMobile}
          >
            Update Amount
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Payouts;